import { CheckIcon, PlusCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

type DataTableBooleanFilterProps = {
    title: string;
    value: boolean | undefined;
    onChange: (value: boolean | undefined) => void;
};

const OPTIONS = [
    { value: 'true', label: 'Oui' },
    { value: 'false', label: 'Non' },
];

export function DataTableBooleanFilter({ title, value, onChange }: DataTableBooleanFilterProps) {
    const isActive = value !== undefined;
    const selectedValue = value === undefined ? null : String(value);

    const handleSelect = (optionValue: string) => {
        if (selectedValue === optionValue) {
            onChange(undefined);
        } else {
            onChange(optionValue === 'true');
        }
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className={cn('h-8 border-dashed bg-filter', isActive && 'bg-filter-active')}
                >
                    <PlusCircle className="size-3.5" />
                    {title}
                    {isActive && (
                        <>
                            <Separator orientation="vertical" className="mx-1 h-4" />
                            <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                                {value ? 'Oui' : 'Non'}
                            </Badge>
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[150px] p-0" align="start">
                <Command>
                    <CommandList>
                        <CommandGroup>
                            {OPTIONS.map((option) => {
                                const isSelected = selectedValue === option.value;
                                return (
                                    <CommandItem
                                        key={option.value}
                                        onSelect={() => handleSelect(option.value)}
                                        data-checked={isSelected}
                                    >
                                        <div
                                            className={cn(
                                                'flex size-4 items-center justify-center rounded-sm border border-primary',
                                                isSelected
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'opacity-50 [&_svg]:invisible',
                                            )}
                                        >
                                            <CheckIcon className="size-3" />
                                        </div>
                                        <span>{option.label}</span>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                    {isActive && (
                        <>
                            <Separator />
                            <CommandGroup>
                                <CommandItem
                                    className="justify-center text-center"
                                    onSelect={() => onChange(undefined)}
                                >
                                    Effacer
                                </CommandItem>
                            </CommandGroup>
                        </>
                    )}
                </Command>
            </PopoverContent>
        </Popover>
    );
}
